// ServiceTypeTable.js

import React, { useState, useEffect } from 'react';
import Axios from 'axios';
import { Host } from '../../App';
import './ServiceTypeTable.css';

const ServiceTypeTable = () => {
  const [serviceTypes, setServiceTypes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [newType, setNewType] = useState({
    name: '',
    category: 'Facility',
    description: '',
    estimated_duration_minutes: 60
  });
  const [isAdmin, setIsAdmin] = useState(false);
  const token = window.localStorage.getItem('acessToken');

  useEffect(() => {
    fetchServiceTypes();
    checkUserPermissions();
  }, []);

  const checkUserPermissions = async () => {
    try {
      const response = await Axios.get(`https://` + Host + `/api/user-info`, {
        params: { token }
      });
      if (response.data) {
        const isLead = response.data.Section === 'Maintenance' &&
          (response.data.Stage === '2' || response.data.Ranked === 'Admin');
        const isAdminUser = response.data.Ranked === 'Admin';
        setIsAdmin(isLead || isAdminUser);
      }
    } catch (error) {
      console.error('Error checking permissions:', error);
    }
  };

  const fetchServiceTypes = async () => {
    setIsLoading(true);
    try {
      const response = await Axios.get(`https://` + Host + `/maintenance/api/service-types`, {
        params: { token }
      });
      if (response.data) {
        setServiceTypes(response.data);
      }
    } catch (error) {
      console.error('Error fetching service types:', error);
      window.alert('Failed to fetch service types');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAdd = async () => {
    if (!newType.name.trim()) {
      window.alert('Please enter a service type name');
      return;
    }

    setIsLoading(true);
    try {
      await Axios.post(`https://` + Host + `/maintenance/api/service-types`, {
        token,
        ...newType
      });
      await fetchServiceTypes();
      setNewType({
        name: '',
        category: 'Facility',
        description: '',
        estimated_duration_minutes: 60
      });
      window.alert('Service type added successfully');
    } catch (error) {
      console.error('Error adding service type:', error);
      window.alert(error.response?.data?.error || 'Failed to add service type');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdate = async (id, updatedData) => {
    setIsLoading(true);
    try {
      await Axios.put(`https://` + Host + `/maintenance/api/service-types/${id}`, updatedData, {
        params: { token }
      });
      await fetchServiceTypes();
      setEditingId(null);
      window.alert('Service type updated successfully');
    } catch (error) {
      console.error('Error updating service type:', error);
      window.alert(error.response?.data?.error || 'Failed to update service type');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Are you sure you want to delete "${name}"?`)) {
      return;
    }

    setIsLoading(true);
    try {
      await Axios.delete(`https://` + Host + `/maintenance/api/service-types/${id}`, {
        params: { token }
      });
      await fetchServiceTypes();
      window.alert('Service type deactivated successfully');
    } catch (error) {
      console.error('Error deleting service type:', error);
      window.alert(error.response?.data?.error || 'Failed to delete service type');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditChange = (id, field, value) => {
    setServiceTypes(prev => prev.map(st =>
      st.id === id ? { ...st, [field]: value } : st
    ));
  };

  if (!isAdmin) {
    return (
      <div className="service-types-container">
        <div className="alert alert-warning">
          You do not have permission to manage service types. Only Maintenance Leads and Admins can access this page.
        </div>
      </div>
    );
  }

  return (
    <div className="service-types-container">
      <div className="service-types-header">
        <h1>Service Types</h1>
        <p>Manage maintenance service categories and their estimated durations.</p>
      </div>

      <div className="add-service-type">
        <h3>Add New Service Type</h3>
        <div className="add-form">
          <div className="form-group">
            <label>Name</label>
            <input
              type="text"
              className="form-control"
              value={newType.name}
              onChange={(e) => setNewType(prev => ({ ...prev, name: e.target.value }))}
              placeholder="Enter service type name"
            />
          </div>
          <div className="form-group">
            <label>Category</label>
            <select
              className="form-control"
              value={newType.category}
              onChange={(e) => setNewType(prev => ({ ...prev, category: e.target.value }))}
            >
              <option value="Facility">Facility</option>
              <option value="Equipment">Equipment</option>
            </select>
          </div>
          <div className="form-group">
            <label>Description</label>
            <input
              type="text"
              className="form-control"
              value={newType.description}
              onChange={(e) => setNewType(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Brief description"
            />
          </div>
          <div className="form-group">
            <label>Est. Duration (min)</label>
            <input
              type="number"
              className="form-control"
              value={newType.estimated_duration_minutes}
              onChange={(e) => setNewType(prev => ({
                ...prev,
                estimated_duration_minutes: parseInt(e.target.value) || 0
              }))}
              min="0"
            />
          </div>
          <button
            className="btn btn-success"
            onClick={handleAdd}
            disabled={isLoading}
          >
            {isLoading ? 'Adding...' : 'Add Service Type'}
          </button>
        </div>
      </div>

      <div className="service-types-table">
        <h3>Existing Service Types</h3>
        {isLoading && (
          <div className="loading-spinner">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        )}
        <table className="table table-bordered table-hover">
          <thead>
            <tr>
              <th>Name</th>
              <th>Category</th>
              <th>Description</th>
              <th>Duration (min)</th>
              <th>Active</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {serviceTypes.length === 0 ? (
              <tr>
                <td colSpan="6" className="text-center">No service types found</td>
              </tr>
            ) : (
              serviceTypes.map((type) => (
                <tr key={type.id}>
                  <td>
                    {editingId === type.id ? (
                      <input
                        type="text"
                        className="form-control"
                        value={type.name}
                        onChange={(e) => handleEditChange(type.id, 'name', e.target.value)}
                      />
                    ) : (
                      type.name
                    )}
                  </td>
                  <td>
                    {editingId === type.id ? (
                      <select
                        className="form-control"
                        value={type.category}
                        onChange={(e) => handleEditChange(type.id, 'category', e.target.value)}
                      >
                        <option value="Facility">Facility</option>
                        <option value="Equipment">Equipment</option>
                      </select>
                    ) : (
                      type.category
                    )}
                  </td>
                  <td>
                    {editingId === type.id ? (
                      <input
                        type="text"
                        className="form-control"
                        value={type.description || ''}
                        onChange={(e) => handleEditChange(type.id, 'description', e.target.value)}
                      />
                    ) : (
                      type.description || '-'
                    )}
                  </td>
                  <td>
                    {editingId === type.id ? (
                      <input
                        type="number"
                        className="form-control"
                        value={type.estimated_duration_minutes}
                        onChange={(e) => handleEditChange(type.id, 'estimated_duration_minutes', parseInt(e.target.value) || 0)}
                        min="0"
                      />
                    ) : (
                      type.estimated_duration_minutes
                    )}
                  </td>
                  <td>
                    {editingId === type.id ? (
                      <select
                        className="form-control"
                        value={type.is_active ? '1' : '0'}
                        onChange={(e) => handleEditChange(type.id, 'is_active', e.target.value === '1')}
                      >
                        <option value="1">Yes</option>
                        <option value="0">No</option>
                      </select>
                    ) : (
                      type.is_active ? '✅' : '❌'
                    )}
                  </td>
                  <td>
                    {editingId === type.id ? (
                      <>
                        <button
                          className="btn btn-sm btn-success"
                          onClick={() => handleUpdate(type.id, type)}
                        >
                          Save
                        </button>
                        <button
                          className="btn btn-sm btn-secondary"
                          onClick={() => setEditingId(null)}
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={() => setEditingId(type.id)}
                        >
                          Edit
                        </button>
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => handleDelete(type.id, type.name)}
                        >
                          Delete
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ServiceTypeTable;